'use strict';

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import MyTabs from '@components/Widgets/Tabs.react';
import NutritionInfo from '@components/Products/NutritionInfo.react';
import FoodUnitsSelector from '@components/DailyLog/FoodUnitsSelectorModal.react';

import { useAssets, useMealPlanData, useMealOperations, usePermissions } from '../../contexts';
import { getPrimaryMeal } from '@utils/Meals';
import { getNutrientsForMeals } from '@utils/Nutrition';

const MealNutritionTabs = ({ sortedMeals }) => {
    // Get data from context
    const { recipes, foods } = useAssets();
    const { plan, profile } = useMealPlanData();
    const { updateMeal } = useMealOperations();
    const { canViewNutrition } = usePermissions();

    // Nutrition calculations
    const allContent = useMemo(() => {
        return { ...recipes, ...foods };
    }, [recipes, foods]);

    const combinedNutrients = useMemo(() => {
        if (!sortedMeals.length || !allContent) return {};
        return getNutrientsForMeals(sortedMeals, allContent, profile.portion, null);
    }, [sortedMeals, allContent, profile.portion]);

    // Handle portion changes
    const handleChangeAmount = useCallback(async (meal, logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters) => {
        const result = await updateMeal(meal.id || meal.uuid, {
            logged_portion,
            logged_unit,
            logged_amount,
            logged_grams,
            logged_milliliters
        });

        if (!result.success) {
            console.error('Failed to update meal portion:', result.error);
        }
    }, [updateMeal]);

    const renderTabs = useCallback(() => {
        // Check if nutrition should be hidden
        if (!canViewNutrition) {
            return null;
        }

        if (!sortedMeals.length) {
            return null;
        }

        const nutrientTabs = [];

        // Add individual dish tabs
        sortedMeals.forEach((meal, i) => {
            const dish = getPrimaryMeal([meal], recipes, foods);

            if (!dish) {
                return;
            }

            const mealNutrients = getNutrientsForMeals([meal], allContent, profile.portion || 1, null);


            nutrientTabs.push(
                <section key={i} data-title={`Dish ${i + 1}`}>
                    {/* Portion selector for individual dishes */}
                    <div className="nutrition-portion">
                        <p className="t2">
                            Showing nutrition for {profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User'}:{' '}
                        </p>
                        <FoodUnitsSelector
                            food={dish.food || dish.recipe}
                            meal={{logged_amount: 1, logged_unit: 'serving', ...meal}}
                            onChangeAmount={(logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters) => {
                                handleChangeAmount(meal, logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters);
                            }}
                            replaceChevronText="edit"
                            howMuchQuestion="Show nutrition for:"
                        />
                    </div>

                    <NutritionInfo
                        profile={{ ...profile, portion: 1 }}
                        nutrients={mealNutrients}
                    />
                </section>
            );
        });

        // Show tabs if multiple dishes, otherwise show single dish
        const tabsContent = nutrientTabs.length > 1 ? (
            <MyTabs defaultTab="Combined" className="pill-tabs" htmlRef="nutrientTabs">
                <section key="combined" data-title="Combined">
                    <NutritionInfo
                        profile={{ ...profile, portion: 1 }}
                        nutrients={combinedNutrients}
                    />
                </section>
                {nutrientTabs}
            </MyTabs>
        ) : (
            nutrientTabs[1] || nutrientTabs[0]
        );

        return tabsContent;
    }, [sortedMeals, profile, combinedNutrients, recipes, foods, allContent, handleChangeAmount, canViewNutrition]);

    return renderTabs();
};

MealNutritionTabs.propTypes = {
    sortedMeals: PropTypes.array.isRequired,
};

export default MealNutritionTabs;
